import { z } from "zod"

export enum VslVersionStatus {
  UPLOADING = "UPLOADING",
  PROCESSING = "PROCESSING",
  READY = "READY",
  FAILED = "FAILED",
}

export enum VslOverlayKind {
  CTA_BUTTON = "CTA_BUTTON",
  FORM_POPUP = "FORM_POPUP",
}

export enum VslCtaAction {
  OPEN_LINK = "OPEN_LINK",
  OPEN_FORM = "OPEN_FORM",
}

export enum VslFormField {
  NAME = "NAME",
  EMAIL = "EMAIL",
  PHONE = "PHONE",
  STORE_NAME = "STORE_NAME",
  MESSAGE = "MESSAGE",
}

export enum VslViewSource {
  STORE_LINK = "STORE_LINK",
  PAGE = "PAGE",
  EMBED = "EMBED",
}

/** In the order the form shows them. */
export const VSL_FORM_FIELDS: VslFormField[] = [
  VslFormField.NAME,
  VslFormField.EMAIL,
  VslFormField.PHONE,
  VslFormField.STORE_NAME,
  VslFormField.MESSAGE,
]

export const VSL_FORM_FIELD_LABELS: Record<VslFormField, string> = {
  [VslFormField.NAME]: "Namn",
  [VslFormField.EMAIL]: "E-post",
  [VslFormField.PHONE]: "Telefon",
  [VslFormField.STORE_NAME]: "Butik",
  [VslFormField.MESSAGE]: "Meddelande",
}

export const VSL_VIEW_SOURCE_LABELS: Record<VslViewSource, string> = {
  [VslViewSource.STORE_LINK]: "Butikslänk",
  [VslViewSource.PAGE]: "Öppen länk",
  [VslViewSource.EMBED]: "Inbäddad",
}

/** What admin calls a VSL's general link, video.julo.se/<slug>. */
export const VSL_GENERAL_LINK_NAME = "Allmän"

/** 75 → "1:15", 3725 → "1:02:05". */
export const formatVslSecond = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = String(seconds % 60).padStart(2, "0")
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${rest}`
  return `${minutes}:${rest}`
}

// --- Admin ------------------------------------------------------------------

export interface VslOverlay {
  id: string
  kind: VslOverlayKind
  startSecond: number
  endSecond: number | null
  label: string | null
  ctaAction: VslCtaAction | null
  url: string | null
  formTitle: string | null
  formFields: VslFormField[]
  requiredFormFields: VslFormField[]
  isRequired: boolean
}

export interface VslVersionChange {
  id: string
  description: string
  createdAt: string
}

export interface VslVersion {
  id: string
  number: number
  label: string | null
  status: VslVersionStatus
  durationSeconds: number | null
  isLive: boolean
  /** Null until Bunny Stream has encoded the file. */
  playbackUrl: string | null
  /** The uploaded thumbnail, or else Bunny Stream's own once encoded. */
  thumbnailUrl: string | null
  hasCustomThumbnail: boolean
  overlays: VslOverlay[]
  /** Newest first. */
  changes: VslVersionChange[]
  createdAt: string
}

export interface VslListItem {
  id: string
  title: string
  slug: string
  pageUrl: string
  liveVersionNumber: number | null
  versionCount: number
  sessionCount: number
  linkCount: number
  createdAt: string
}

export interface VslDetail {
  id: string
  title: string
  slug: string
  /** The anonymous page. */
  pageUrl: string
  /** HTML to paste where the player should be embedded. */
  embedSnippet: string
  notificationEmail: string | null
  notifyWatchedPercent: number | null
  /** Newest first. */
  versions: VslVersion[]
}

export interface VslUploadCredentials {
  versionId: string
  bunnyVideoId: string
  libraryId: string
  /** Unix seconds. */
  expirationTime: number
  signature: string
}

/**
 * A store link, a custom link (no store) with a name of its own, or the
 * VSL's general link, "Allmän", on video.julo.se/<slug>.
 */
export interface VslLinkItem {
  id: string
  /** Null on custom links and the general link. */
  storeId: string | null
  isGeneral: boolean
  /** The store's name, the custom link's, or "Allmän". */
  name: string
  url: string
  createdAt: string
}

export interface VslMetrics {
  sessions: number
  uniqueViewers: number
  /** Rewatches included. */
  watchedSeconds: number
  /** Average over sessions of the share of the version seen at least once. */
  averagePercentWatched: number
  /** Sessions that saw at least 90 % of the version. */
  completedSessions: number
  /** Store links and custom links watched; the general link not counted. */
  stores: number
  ctaClicks: number
  formSubmissions: number
}

/** A store link's, custom link's or the general link's stats. */
export interface VslStoreStatsRow {
  linkId: string
  /** Null on custom links and the general link. */
  storeId: string | null
  isGeneral: boolean
  /** The store's name, the custom link's, or "Allmän". */
  name: string
  url: string
  sessions: number
  /** Browsers that opened the link; one person may use several. */
  viewers: number
  watchedSeconds: number
  /**
   * The most of a version one viewer has seen, over all their sessions on
   * the link. Across versions: the highest of them.
   */
  percentWatched: number
  versionNumbers: number[]
  ctaClicks: number
  formSubmissions: number
  /** Null if the link has not been opened. */
  lastWatchedAt: string | null
}

/**
 * One ad's stats: the sessions that came with its `utm_source` and
 * `utm_content`. Works for any ad platform whose links carry utm_*.
 */
export interface VslAdStatsRow {
  /** The `utm_source` parameter, e.g. "fb", "google" or "linkedin". */
  source: string | null
  /** The `utm_content` parameter; with Meta's defaults, the ad's ID. */
  adId: string
  /** The latest name seen for the ad, if its URL carries `ad_name`. */
  adName: string | null
  /** The `utm_campaign` parameter; with Meta's defaults, the campaign's ID. */
  campaign: string | null
  /** The `utm_term` parameter; with Meta's defaults, the ad set's ID. */
  adGroup: string | null
  sessions: number
  viewers: number
  averagePercentWatched: number
  /** Sessions that saw at least 90 % of the version. */
  completedSessions: number
  ctaClicks: number
  formSubmissions: number
  lastWatchedAt: string
}

export interface VslVersionMetrics extends VslMetrics {
  versionId: string
  number: number
  label: string | null
  isLive: boolean
  durationSeconds: number | null
}

export interface VslStats {
  total: VslMetrics
  /** Newest first. */
  versions: VslVersionMetrics[]
  /** Every store link, opened or not, most recently watched first. */
  stores: VslStoreStatsRow[]
  /** Ads that brought viewers, most sessions first. */
  ads: VslAdStatsRow[]
}

export interface VslHeatmapSecond {
  second: number
  /** Sessions that played this second at least once. */
  viewers: number
  /** Times it was played, rewatches included. */
  plays: number
}

export interface VslOverlayStats {
  overlayId: string
  kind: VslOverlayKind
  /** The button's label or the form's title. */
  title: string
  startSecond: number
  /** Sessions that got to `startSecond`. */
  reached: number
  ctaClicks: number
  formSubmissions: number
}

export interface VslVersionStats {
  vslId: string
  vslTitle: string
  version: VslVersion
  metrics: VslMetrics
  heatmap: VslHeatmapSecond[]
  overlays: VslOverlayStats[]
  /** Store links that played this version. */
  stores: VslStoreStatsRow[]
}

/** What one viewer played of one version, over all their sessions. */
export interface VslViewerTimeline {
  versionNumber: number
  durationSeconds: number
  /** Times the viewer opened the link on this version. */
  sessions: number
  watchedSeconds: number
  percentWatched: number
  /** How many times each second was played; index = second. */
  secondCounts: number[]
  ctaClicks: { label: string; second: number }[]
  formSubmissionSeconds: number[]
  firstSeenAt: string
  lastActiveAt: string
}

/**
 * One browser that opened the link. Numbered in the order they first came,
 * so "Tittare 2" stays the same browser; one person may use several.
 */
export interface VslLinkViewer {
  viewerId: string
  number: number
  /** The most of a version the viewer has seen. */
  percentWatched: number
  lastActiveAt: string
  sources: VslViewSource[]
  /** Newest version first. */
  timelines: VslViewerTimeline[]
}

export interface VslLinkViewers {
  /** The store's name, the custom link's, or "Allmän". */
  name: string
  vslTitle: string
  /** Most recently active first. */
  viewers: VslLinkViewer[]
}

export interface VslFormSubmissionItem {
  id: string
  createdAt: string
  versionNumber: number
  source: VslViewSource
  /** The store link, custom link or general link it came through. */
  linkName: string
  second: number
  name: string | null
  email: string | null
  phone: string | null
  storeName: string | null
  message: string | null
}

/** No capitals: link tokens always have one, so the two never clash. */
const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** video.julo.se/embed/<slug> is the embedded player. */
const RESERVED_SLUGS = ["embed"]
/** video.julo.se/v1 etc. would reach the API's own routes, not the VSL. */
const API_VERSION_SLUG_REGEX = /^v\d+$/

export const CreateVslSchema = z.object({
  title: z.string().trim().min(1).max(255),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(SLUG_REGEX, "Bara små bokstäver, siffror och bindestreck")
    .refine(
      (slug) =>
        !RESERVED_SLUGS.includes(slug) && !API_VERSION_SLUG_REGEX.test(slug),
      "Adressen är reserverad",
    ),
})

export type CreateVsl = z.infer<typeof CreateVslSchema>

export const UpdateVslSchema = CreateVslSchema.extend({
  notificationEmail: z.string().email().max(255).nullable(),
  notifyWatchedPercent: z.number().int().min(1).max(100).nullable(),
})

export type UpdateVsl = z.infer<typeof UpdateVslSchema>

export const CreateVslVersionSchema = z.object({
  label: z.string().trim().max(255).nullable(),
})

export type CreateVslVersion = z.infer<typeof CreateVslVersionSchema>

export const VslOverlayInputSchema = z
  .object({
    /** Set on overlays that already exist, so their stats carry over. */
    id: z.string().uuid().nullable(),
    kind: z.nativeEnum(VslOverlayKind),
    startSecond: z.number().int().min(0),
    endSecond: z.number().int().min(0).nullable(),
    label: z.string().trim().max(255).nullable(),
    ctaAction: z.nativeEnum(VslCtaAction).nullable(),
    url: z.string().url().max(2048).nullable(),
    formTitle: z.string().trim().max(255).nullable(),
    formFields: z.array(z.nativeEnum(VslFormField)),
    requiredFormFields: z.array(z.nativeEnum(VslFormField)),
    isRequired: z.boolean(),
  })
  .superRefine((overlay, ctx) => {
    const isCta = overlay.kind === VslOverlayKind.CTA_BUTTON
    const hasForm = !isCta || overlay.ctaAction === VslCtaAction.OPEN_FORM
    if (isCta && !overlay.label) {
      ctx.addIssue({
        code: "custom",
        path: ["label"],
        message: "Knappen behöver en text",
      })
    }
    if (isCta && !overlay.ctaAction) {
      ctx.addIssue({
        code: "custom",
        path: ["ctaAction"],
        message: "Välj vad knappen gör",
      })
    }
    if (
      isCta &&
      overlay.ctaAction === VslCtaAction.OPEN_LINK &&
      overlay.url === null
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["url"],
        message: "Ange en länk",
      })
    }
    if (
      isCta &&
      overlay.endSecond !== null &&
      overlay.endSecond <= overlay.startSecond
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["endSecond"],
        message: "Måste vara efter starten",
      })
    }
    if (hasForm && overlay.formFields.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["formFields"],
        message: "Välj minst ett fält",
      })
    }
    if (
      overlay.requiredFormFields.some(
        (field) => !overlay.formFields.includes(field),
      )
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["requiredFormFields"],
        message: "Obligatoriska fält måste finnas i formuläret",
      })
    }
  })

export type VslOverlayInput = z.infer<typeof VslOverlayInputSchema>

export const UpdateVslVersionSchema = z.object({
  label: z.string().trim().max(255).nullable(),
  overlays: z.array(VslOverlayInputSchema).max(50),
})

export type UpdateVslVersion = z.infer<typeof UpdateVslVersionSchema>

export const CreateVslLinksSchema = z.object({
  storeIds: z.array(z.string().uuid()).min(1).max(500),
})

export type CreateVslLinks = z.infer<typeof CreateVslLinksSchema>

/** Its address, video.julo.se/<token>, is made up by the API. */
export const CreateVslCustomLinkSchema = z.object({
  name: z.string().trim().min(1).max(100),
})

export type CreateVslCustomLink = z.infer<typeof CreateVslCustomLinkSchema>

/** No link: the general link. */
export const VslEmailHtmlQuerySchema = z.object({
  linkId: z.string().uuid().optional(),
})

export type VslEmailHtmlQuery = z.infer<typeof VslEmailHtmlQuerySchema>

export interface VslEmailHtml {
  /**
   * A page holding only the thumbnail with a play button, linked to the
   * video: select all, copy and paste it into Gmail.
   */
  html: string
}

// --- video.julo.se ----------------------------------------------------------

export interface VslPublicOverlay {
  id: string
  kind: VslOverlayKind
  startSecond: number
  endSecond: number | null
  label: string | null
  ctaAction: VslCtaAction | null
  url: string | null
  formTitle: string | null
  formFields: VslFormField[]
  requiredFormFields: VslFormField[]
  isRequired: boolean
  /** The viewer has already sent this form, so it is not shown again. */
  isSubmitted: boolean
}

export interface VslPlayback {
  sessionId: string
  /** Kept in the browser and sent with the next session. */
  viewerId: string
  title: string
  durationSeconds: number
  playbackUrl: string
  thumbnailUrl: string
  overlays: VslPublicOverlay[]
  /** Where the viewer left off last time; null to start from the beginning. */
  resumeSecond: number | null
  /** Filled in on store links. */
  storeName: string | null
}

/**
 * The ad that brought the viewer, from the page's URL parameters. With
 * Meta's default URL parameters these are IDs: `utmContent` is the ad,
 * `utmTerm` the ad set and `utmCampaign` the campaign.
 */
export const VslAttributionSchema = z.object({
  utmSource: z.string().max(255).nullable(),
  utmMedium: z.string().max(255).nullable(),
  utmCampaign: z.string().max(255).nullable(),
  utmTerm: z.string().max(255).nullable(),
  utmContent: z.string().max(255).nullable(),
  /** From an `ad_name={{ad.name}}` parameter on the ad's URL. */
  adName: z.string().max(255).nullable(),
})

export type VslAttribution = z.infer<typeof VslAttributionSchema>

/** The URL parameter behind each attribution field. */
export const VSL_ATTRIBUTION_PARAMS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmTerm: "utm_term",
  utmContent: "utm_content",
  adName: "ad_name",
} satisfies Record<keyof VslAttribution, string>

export const StartVslSessionSchema = z.object({
  /**
   * What follows video.julo.se/ (or /embed/): a link's token, or a VSL's
   * slug for its general link.
   */
  address: z.string().min(1).max(100),
  viewerId: z.string().uuid().nullable(),
  isEmbed: z.boolean(),
  referrer: z.string().max(2048).nullable(),
  /** Defaulted for players built before attribution existed. */
  attribution: VslAttributionSchema.default({
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmTerm: null,
    utmContent: null,
    adName: null,
  }),
})

export type StartVslSession = z.infer<typeof StartVslSessionSchema>

export const RecordVslProgressSchema = z.object({
  /**
   * Each second played since the last report, one entry per play: a second
   * watched twice is listed twice.
   */
  seconds: z.array(z.number().int().min(0)).max(600),
  /** Where the viewer is now. */
  currentSecond: z.number().int().min(0),
})

export type RecordVslProgress = z.infer<typeof RecordVslProgressSchema>

export const RecordVslCtaClickSchema = z.object({
  overlayId: z.string().uuid(),
  second: z.number().int().min(0),
})

export type RecordVslCtaClick = z.infer<typeof RecordVslCtaClickSchema>

export const SubmitVslFormSchema = z.object({
  overlayId: z.string().uuid(),
  second: z.number().int().min(0),
  name: z.string().trim().max(255).nullable(),
  email: z.string().trim().email().max(255).nullable(),
  phone: z.string().trim().max(50).nullable(),
  storeName: z.string().trim().max(255).nullable(),
  message: z.string().trim().max(5000).nullable(),
})

export type SubmitVslForm = z.infer<typeof SubmitVslFormSchema>
